import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'
import { kit } from './strings'
import './InfoHint.css'

/**
 * A small "i" that reveals help text on hover, focus or tap.
 *
 * Long guidance paragraphs used to sit permanently under the control they
 * explain — read once, skipped forever after, and costing the form a line every
 * time. Behind this icon the text stays one hover away and the form reads as a
 * form.
 *
 * The bubble is portalled to <body> and fixed-positioned so a drawer, a card's
 * overflow or a table cell can never clip it.
 *
 * @param {import('react').ReactNode} content  the help text
 * @param {string} [label]  accessible name for the trigger
 */
export default function InfoHint({ content, label = kit.moreInfo, className = '' }) {
  const triggerRef = useRef(null)
  /** `null` while closed; otherwise the resolved screen position. */
  const [pos, setPos] = useState(null)
  const tipId = useId()

  const place = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    // Flip below the icon when there is no room for the bubble above it.
    const below = rect.top < 140
    setPos({
      left: Math.round(rect.left + rect.width / 2),
      top: Math.round(below ? rect.bottom : rect.top),
      placement: below ? 'below' : 'above',
    })
  }, [])

  const close = useCallback(() => setPos(null), [])

  // Keep the bubble anchored while it is open: the field under it may scroll
  // inside a drawer, and the window may resize.
  useEffect(() => {
    if (!pos) return undefined
    const reposition = () => place()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [pos, place])

  const open = pos != null

  return (
    <span className={`info-hint ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className="info-hint__trigger"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={place}
        onMouseLeave={close}
        onFocus={place}
        onBlur={close}
        onClick={(event) => {
          // It may sit inside a <label>; do not forward the click to the control.
          event.preventDefault()
          event.stopPropagation()
          if (open) close()
          else place()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') close()
        }}
      >
        <Info size={14} strokeWidth={2} aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <span
            id={tipId}
            role="tooltip"
            className={`info-hint__tip info-hint__tip--${pos.placement}`}
            style={{ top: pos.top, left: pos.left }}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  )
}
